"use client";
import "@/lib/storyblok/storyblokSetup/blokSetup/bloks";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PropsWithChildren, useState } from "react";

import ShowroomModal from "@/components/atoms/ShowroomModal/ShowroomModal";
import Device from "@/components/tools/Device";
import env from "@/env.mjs";
import ScrollManager from "@/lib/centra/contextWrappers/scrollManager";
import useLegacySelection from "@/lib/hooks/useSelection";
import usePersistedStore from "@/lib/stateManagement/persistedState/persistedStore";
import { CentraClientProvider } from "@frend-digital/centra/client";
import { NextUIProvider } from "@frend-digital/ui/next";
import Script from "next/script";
import Events from "./_helpers/events";

// For now we escape this as dynamic to avoid rewrite due to mismatches

type Props = {
  children: React.ReactNode;
};

const RootLayoutClient = ({ children }: Props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Note: option to prevent excessive refetching
            // TODO: Consider setting this option as default in tanstack query configuration
            refetchOnMount: false,
          },
        },
      })
  );

  // declared as var here to avoid repetition of shared code for dev and prod
  const defaultReturn = (
    <QueryClientProvider client={queryClient}>
      <NextUIProvider>
        <ScrollManager>
          <Device>
            <NewCentraProvider>
              <Events />
              {children}
            </NewCentraProvider>
            <CentraCheckoutScript />
            <ShowroomModal />
          </Device>
        </ScrollManager>
        <ReactQueryDevtools initialIsOpen={false} />
      </NextUIProvider>
    </QueryClientProvider>
  );

  //

  return defaultReturn;
};

const CentraCheckoutScript = () => {
  const { data } = useLegacySelection();

  if (!data?.selection?.centraCheckoutScript) return null;

  return (
    <Script id="centra-checkout-script">
      {`${data.selection.centraCheckoutScript}`}
    </Script>
  );
};

const NewCentraProvider = ({ children }: PropsWithChildren) => {
  const token = usePersistedStore((s) => s.token);
  return (
    <CentraClientProvider
      apiUrl={env.NEXT_PUBLIC_CENTRA_CHECKOUT_API}
      overrideToken={token}
    >
      {children}
    </CentraClientProvider>
  );
};

export default RootLayoutClient;
